import { useState, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-only: manage the Batch Checker sacrifice pool.
 * Targets: 7 verified ISBN books, 9 verified DVDs, 0 CDs (for now).
 */
export default function SacrificePoolPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ barcode: '', category: 'isbn_book', mm_value: '', note: '' });
  const [err, setErr] = useState('');
  const [minerResult, setMinerResult] = useState(null);
  const [mining, setMining] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/batches/pool/list`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
      else setErr('Failed to load pool.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setErr('');
    const body = {
      barcode: form.barcode.replace(/\D/g, ''),
      category: form.category,
      mm_value: form.mm_value ? parseFloat(form.mm_value) : null,
      note: form.note.trim() || null,
    };
    const res = await fetch(`${API}/api/batches/pool`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const j = await res.json();
    if (!res.ok) setErr(j.detail || 'Could not add.');
    else {
      setForm({ barcode: '', category: 'isbn_book', mm_value: '', note: '' });
      refresh();
    }
  };
  const verify = async (barcode) => {
    await fetch(`${API}/api/batches/pool/${barcode}/verify`, { method: 'POST', credentials: 'include' });
    refresh();
  };
  const remove = async (barcode) => {
    if (!window.confirm('Remove this barcode from the sacrifice pool?')) return;
    await fetch(`${API}/api/batches/pool/${barcode}`, { method: 'DELETE', credentials: 'include' });
    refresh();
  };

  const runMiner = async () => {
    setMining(true);
    setMinerResult(null);
    try {
      const res = await fetch(`${API}/api/admin/mine-sacrifice-pool`, {
        method: 'POST',
        credentials: 'include',
      });
      const j = await res.json();
      setMinerResult(j.stats || j);
      refresh();
    } finally {
      setMining(false);
    }
  };

  if (loading) return <p className="text-zinc-500">Loading…</p>;

  const counts = data?.counts || {};
  const verifiedCounts = data?.verified_counts || {};
  const targets = data?.targets || { isbn_book: 7, dvd: 9, cd: 0 };
  const items = data?.items || [];

  return (
    <div data-testid="sacrifice-pool-panel">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-5">
        <h3 className="text-base font-bold text-white mb-3">Pool Health</h3>
        <div className="grid grid-cols-3 gap-3 text-sm">
          {['isbn_book', 'dvd', 'cd'].map((cat) => {
            const target = targets[cat] || 0;
            const verified = verifiedCounts[cat] || 0;
            const total = counts[cat] || 0;
            const ok = verified >= target;
            return (
              <div
                key={cat}
                className={`bg-black border rounded-lg p-3 ${
                  ok ? 'border-[#00D26A]' : target > 0 ? 'border-[#E04A3F]/60' : 'border-zinc-800'
                }`}
                data-testid={`pool-health-${cat}`}
              >
                <div className="text-xs text-zinc-500 uppercase">{cat.replace('_', ' ')}</div>
                <div className="text-lg font-extrabold text-white mt-1">
                  {verified}
                  <span className="text-sm text-zinc-500"> / {target}</span>
                </div>
                <div className="text-[11px] text-zinc-500">{total} total, {verified} verified</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <span>⛏️</span> Auto-miner
            </h3>
            <p className="text-xs text-zinc-500 mt-1">
              Scans your media library for barcodes matching the sacrifice criteria
              (MM=1p · WBB=0 active · WoB=0 active) and files them as unverified candidates.
              Runs automatically at 04:00 UK time daily — trigger on demand below.
            </p>
          </div>
          <button
            onClick={runMiner}
            disabled={mining}
            className="px-4 py-2 rounded-full font-bold text-sm whitespace-nowrap disabled:opacity-50"
            style={{ background: 'linear-gradient(90deg,#00D26A,#FFD700)', color: '#050505' }}
            data-testid="run-miner-btn"
          >
            {mining ? 'Mining…' : 'Run miner now'}
          </button>
        </div>
        {minerResult && (
          <div className="bg-black border border-zinc-800 rounded-lg p-3 text-xs text-zinc-300" data-testid="miner-result">
            Scanned <strong className="text-white">{minerResult.scanned}</strong> ·
            Matched <strong className="text-[#FFD700]">{minerResult.matched}</strong> ·
            Inserted <strong className="text-[#00D26A]">{minerResult.inserted}</strong> ·
            Skipped (already in pool) <strong>{minerResult.skipped_in_pool}</strong>
          </div>
        )}
      </div>

      <form
        onSubmit={add}
        className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-5"
        data-testid="sacrifice-pool-form"
      >
        <h3 className="text-base font-bold text-white mb-3">Add candidate barcode</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input
            required
            value={form.barcode}
            onChange={(e) => setForm({ ...form, barcode: e.target.value })}
            placeholder="Barcode"
            className="bg-black border border-zinc-700 rounded px-3 py-2 font-mono"
            data-testid="pool-barcode-input"
          />
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="bg-black border border-zinc-700 rounded px-3 py-2"
            data-testid="pool-category-select"
          >
            <option value="isbn_book">ISBN book</option>
            <option value="dvd">DVD / Blu-ray</option>
            <option value="cd">CD</option>
          </select>
          <input
            value={form.mm_value}
            onChange={(e) => setForm({ ...form, mm_value: e.target.value })}
            placeholder="MM £ (opt)"
            type="number"
            step="0.01"
            className="bg-black border border-zinc-700 rounded px-3 py-2"
            data-testid="pool-mm-value-input"
          />
          <button
            type="submit"
            className="rounded-full font-bold text-sm"
            style={{ background: 'linear-gradient(90deg,#00D26A,#FFD700)', color: '#050505' }}
            data-testid="pool-add-btn"
          >
            Add
          </button>
        </div>
        <input
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          placeholder="Note (optional, title/source etc.)"
          className="w-full mt-2 bg-black border border-zinc-700 rounded px-3 py-2 text-sm"
          data-testid="pool-note-input"
        />
        {err && <p className="text-sm text-red-400 mt-2" data-testid="pool-add-error">{err}</p>}
      </form>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <h3 className="text-base font-bold text-white mb-3">Pool ({items.length})</h3>
        {items.length === 0 ? (
          <p className="text-zinc-500 italic" data-testid="pool-empty">No barcodes in pool yet.</p>
        ) : (
          <ul className="space-y-2" data-testid="pool-list">
            {items.map((it) => (
              <li
                key={it.barcode}
                className="flex items-center justify-between gap-3 bg-black border border-zinc-800 rounded-lg px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <p className="font-mono text-zinc-300">{it.barcode}</p>
                  <p className="text-[11px] text-zinc-500">
                    {it.category} · MM {it.mm_value != null ? `£${it.mm_value}` : '—'}
                    {it.note && <span className="italic ml-2">“{it.note}”</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {it.verified ? (
                    <span
                      className="text-[10px] font-black px-2 py-0.5 rounded-full"
                      style={{ background: '#00D26A', color: '#050505' }}
                    >
                      VERIFIED
                    </span>
                  ) : (
                    <button
                      onClick={() => verify(it.barcode)}
                      className="text-[11px] font-bold text-[#FFD700] border border-[#FFD700]/50 rounded-full px-3 py-1"
                      data-testid={`pool-verify-${it.barcode}`}
                    >
                      Verify
                    </button>
                  )}
                  <button
                    onClick={() => remove(it.barcode)}
                    className="text-[11px] text-zinc-500 hover:text-red-400"
                    data-testid={`pool-remove-${it.barcode}`}
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
